import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DOCUMENT, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Dictionary, ThemeListItem } from '@key-telematics/fleet-api-client';
import { AppService } from 'app/app.service';
import { KuiActionModule } from 'app/key-ui/action/action.module';
import { IKuiDropdownMenuItem, KuiDropdownModule } from 'app/key-ui/dropdown';
import { KuiIconModule } from 'app/key-ui/icon/icon.module';
import { LocalStore } from 'app/services';
import { firstValueFrom } from 'rxjs';

// The canonical theme to develop against, held as an asset so it can be pasted to and from a theme's
// JSON settings in administration. Names the file as well as the theme it is applied as.
const KINESIS_THEME_ID = 'kinesis';

// What the picker remembers between sessions. Absent means the client's configured theme applies.
const OVERRIDE_KEY = 'themeId';

@Component({
    selector: 'key-theme-switcher',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [KuiActionModule, KuiDropdownModule, KuiIconModule],
    host: { '[class.menu-footer-action]': 'enabled()' },
    template: `
        @if (enabled()) {
            <kui-dropdown [style]="'none'" [menu]="menu()">
                <kui-icon class="ml-1" kui-action size="sm" name="palette" title="Theme"></kui-icon>
            </kui-dropdown>
        }
    `,
    styles: `
        :host {
            display: none;
        }

        // Matches the collapse arrow it sits beside, so the two read as one footer bar. The class is
        // in the selector to outrank the menu's '.app-menu * { border-width }' reset.
        :host(.menu-footer-action) {
            display: flex;
            align-items: center;

            border-top-style: solid;
            border-top-width: var(--width-border);

            padding: var(--padding);

            color: var(--footer-font-color);
            background-color: var(--footer-background-color);
            height: var(--height-header);
        }
    `,
})
export class KeyThemeSwitcherComponent {

    private app = inject(AppService);
    private http = inject(HttpClient);
    private document = inject(DOCUMENT);
    private store = new LocalStore('theme-switcher');

    readonly collapsed = input(false);
    readonly enabled = computed(() => this.app.isLocalhost && !this.collapsed());

    private themes = signal<ThemeListItem[]>([]);
    private override = signal<string>(this.store.get<string>(OVERRIDE_KEY));

    private kinesis: Promise<Dictionary>;

    readonly menu = computed<IKuiDropdownMenuItem[]>(() => {
        const items: IKuiDropdownMenuItem[] = [
            { type: 'heading', text: 'Development' },
            {
                type: 'radio',
                text: 'Default',
                checked: !this.override(),
                closeDropdownOnClicked: true,
                action: () => this.selectDefault(),
            },
        ];
        items.push({
            type: 'radio',
            text: 'Kinesis',
            checked: this.override() === KINESIS_THEME_ID,
            closeDropdownOnClicked: true,
            action: () => this.selectTheme(KINESIS_THEME_ID),
        });
        items.push(
            { type: 'divider' },
            { type: 'heading', text: 'Client themes' },
            ...this.themes().map(item => ({
                type: 'radio',
                text: item.name,
                checked: this.override() === item.id,
                closeDropdownOnClicked: true,
                action: () => this.selectTheme(item.id),
            })),
        );
        return items;
    });

    constructor() {
        if (this.app.isLocalhost) {
            this.app.client$.pipe(takeUntilDestroyed()).subscribe(client => {
                if (client) {
                    this.app.api.entities.listThemes(client.id, true, 0, 100, 'name')
                        .then(result => this.themes.set(result.items))
                        .catch(() => this.themes.set([]));
                }
            });

            // an override outranks whatever else applies a theme, so that it survives the client
            // theme being applied on startup and on every client change
            this.app.theme$.pipe(takeUntilDestroyed()).subscribe(theme => {
                if (this.override() && this.override() !== theme.id) {
                    this.applyOverride(this.override());
                }
            });
        }
    }

    private selectTheme(id: string): void {
        this.store.set(OVERRIDE_KEY, id);
        this.override.set(id);
        this.applyOverride(id);
    }

    private selectDefault(): void {
        this.clearOverride();

        const client = this.app.client;
        if (client?.theme?.id && client.theme.id !== 'inherited') {
            this.applyTheme(client.theme.id, client.logoPath);
        } else {
            this.app.getDomainTheme(this.document.location.hostname);
        }
    }

    private applyOverride(id: string): void {
        if (id === KINESIS_THEME_ID) {
            this.applyKinesis();
        } else {
            this.applyTheme(id);
        }
    }

    private clearOverride(): void {
        this.store.set(OVERRIDE_KEY, null);
        this.override.set(null);
    }

    private applyTheme(id: string, logoPath?: string): void {
        this.app.api.entities.getTheme(id)
            .then(theme => this.app.setTheme({ ...theme, logoPath }))
            .catch(() => this.clearOverride()); // a theme this client cannot see must not keep fighting
    }

    private applyKinesis(): void {
        this.getKinesisSettings()
            .then(settings => this.app.setTheme({
                id: KINESIS_THEME_ID,
                name: 'Kinesis',
                theme: 'default',
                owner: null,
                entity: null,
                settings,
            }))
            .catch(err => {
                console.error('unable to read the kinesis theme', err);
                this.clearOverride();
            });
    }

    // held as a promise rather than a signal so that applying the override never depends on the
    // request having already landed. the timestamp keeps the browser from serving a stale copy
    // after the file is edited by hand.
    private getKinesisSettings(): Promise<Dictionary> {
        if (!this.kinesis) {
            this.kinesis = firstValueFrom(this.http.get<Dictionary>(`assets/themes/${KINESIS_THEME_ID}.json?v=${Date.now()}`));
        }
        return this.kinesis;
    }

}
